import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { API, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, RefreshCw, FileDown, Copy, Check, Mail, Target,
  FileText, TrendingUp, Globe,
} from "lucide-react";

export default function DomainDetail() {
  const { did } = useParams();
  const { t, lang, quota, refreshMe } = useApp();
  const navigate = useNavigate();

  const [domain, setDomain] = useState(null);
  const [pitch, setPitch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/domains/${did}`);
      setDomain(data.domain);
      setPitch(data.pitch);
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
      navigate("/app/portfolios");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [did]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/domains/${did}/generate_pitch`, { language: lang });
      setPitch(data.pitch);
      await refreshMe();
      await load();
      toast.success(t("pitchGenerated"));
    } catch (err) {
      if (err?.response?.status === 402) toast.error(t("quotaReached"));
      else toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setGenerating(false);
    }
  };

  const copyEmail = async () => {
    if (!pitch?.outbound_email) return;
    try {
      await navigator.clipboard.writeText(pitch.outbound_email);
      setCopied(true);
      toast.success(t("copied"));
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error(t("errGeneric"));
    }
  };

  const exportPdf = async () => {
    if (!quota?.pdf_export) { toast.error(t("featureLocked")); navigate("/app/pricing"); return; }
    try {
      const token = localStorage.getItem("dp_token");
      const res = await fetch(`${API}/domains/${did}/pitch/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) { toast.error(t("featureLocked")); navigate("/app/pricing"); return; }
      if (!res.ok) throw new Error("pdf failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${domain.domain_name.replace(/\./g, "_")}_pitch.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errMsg(err, t("errGeneric"))); }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-40" />
        <div className="grid gap-6 xl:grid-cols-12">
          <Skeleton className="h-96 xl:col-span-8" />
          <Skeleton className="h-64 xl:col-span-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb + header */}
      <div>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("back")}
        </button>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Globe className="h-5 w-5" /></span>
            <div>
              <h1 className="font-heading font-mono text-2xl font-semibold tracking-tight" data-testid="domain-detail-name">{domain?.domain_name}</h1>
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={domain?.status} />
                {domain?.category && <span className="text-sm text-muted-foreground">{domain.category}</span>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {pitch ? (
              <>
                <Button variant="secondary" className="gap-2" onClick={generate} disabled={generating} data-testid="domain-detail-regenerate-button">
                  <RefreshCw className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} /> {generating ? t("generating") : t("regenerate")}
                </Button>
                <Button variant="outline" className="gap-2" onClick={exportPdf} data-testid="domain-detail-export-pdf-button">
                  <FileDown className="h-4 w-4" /> {t("exportPdf")}
                </Button>
              </>
            ) : (
              <Button className="gap-2" onClick={generate} disabled={generating} data-testid="domain-detail-generate-button">
                <Sparkles className="h-4 w-4" /> {generating ? t("generating") : t("generatePitch")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {!pitch ? (
        <Card className="flex flex-col items-center gap-3 p-14 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><Sparkles className="h-7 w-7" /></span>
          <h3 className="font-heading text-xl font-semibold">{t("noPitchYet")}</h3>
          <p className="max-w-md text-sm text-muted-foreground">{t("noPitchDesc")}</p>
          <Button className="mt-2 gap-2" onClick={generate} disabled={generating} data-testid="domain-detail-generate-empty-button">
            <Sparkles className="h-4 w-4" /> {generating ? t("generating") : t("generatePitch")}
          </Button>
        </Card>
      ) : (
        <div className="grid gap-6 xl:grid-cols-12">
          {/* Left: tabs */}
          <div className="xl:col-span-8">
            <Card className="p-5">
              <Tabs defaultValue="pitch">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="pitch" data-testid="tab-pitch"><FileText className="mr-1.5 h-4 w-4" /><span className="hidden sm:inline">{t("tabPitch")}</span></TabsTrigger>
                  <TabsTrigger value="icp" data-testid="tab-icp"><Target className="mr-1.5 h-4 w-4" /><span className="hidden sm:inline">{t("tabIcp")}</span></TabsTrigger>
                  <TabsTrigger value="email" data-testid="tab-email"><Mail className="mr-1.5 h-4 w-4" /><span className="hidden sm:inline">{t("tabEmail")}</span></TabsTrigger>
                  <TabsTrigger value="valuation" data-testid="tab-valuation"><TrendingUp className="mr-1.5 h-4 w-4" /><span className="hidden sm:inline">{t("tabValuation")}</span></TabsTrigger>
                </TabsList>

                <TabsContent value="pitch" className="mt-5">
                  <h3 className="font-heading text-lg font-semibold">{t("tabPitch")}</h3>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/85" data-testid="pitch-concept-text">{pitch.concept_text}</p>
                </TabsContent>

                <TabsContent value="icp" className="mt-5">
                  <h3 className="font-heading text-lg font-semibold">{t("tabIcp")}</h3>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/85" data-testid="pitch-icp-text">{pitch.target_icp}</p>
                </TabsContent>

                <TabsContent value="email" className="mt-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-semibold">{t("tabEmail")}</h3>
                    <Button size="sm" variant="secondary" className="gap-1.5" onClick={copyEmail} data-testid="domain-detail-copy-email-button" aria-label={t("copyEmail")}>
                      {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                      {copied ? t("copied") : t("copyEmail")}
                    </Button>
                  </div>
                  <div className="mt-3 rounded-lg border bg-secondary/40 p-4">
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/85" data-testid="domain-detail-outbound-email">{pitch.outbound_email}</pre>
                  </div>
                </TabsContent>

                <TabsContent value="valuation" className="mt-5">
                  <h3 className="font-heading text-lg font-semibold">{t("tabValuation")}</h3>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/85" data-testid="pitch-valuation-text">{pitch.valuation_range}</p>
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Right: meta */}
          <div className="xl:col-span-4">
            <Card className="p-5">
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t("domainMeta")}</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="text-xs text-muted-foreground">{t("colStatus")}</div>
                  <div className="mt-1"><StatusBadge status={domain?.status} /></div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">{t("colCategory")}</div>
                  <div className="mt-1 text-sm">{domain?.category || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">{t("colPrice")}</div>
                  <div className="mt-1 font-mono text-sm">{domain?.target_price ? `€${Number(domain.target_price).toLocaleString()}` : "—"}</div>
                </div>
                <div className="rounded-lg border bg-primary/5 p-3">
                  <div className="text-xs font-medium text-muted-foreground">{t("valuationRange")}</div>
                  <div className="mt-1 font-mono text-base font-semibold text-primary">{pitch.valuation_range}</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
